import React, { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import WikiLayout from '../../components/layout/WikiLayout';

const Breadcrumbs = ({ title }) => (
  <nav className="flex items-center space-x-2 text-sm text-base-content/60">
    <Link to="/wiki" className="flex items-center hover:text-primary">
      <BookOpenIcon className="w-4 h-4 mr-1" />
      <span>Wiki</span>
    </Link>
    <ChevronRightIcon className="w-4 h-4" />
    <span className="text-base-content/80">{title}</span>
  </nav>
);

// Hilfsfunktion zum Erstellen von IDs für Überschriften
const createHeadingId = (text) => {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Custom components for markdown rendering with DaisyUI styling
const components = {
  // Headers
  h1: ({ children }) => {
    const id = createHeadingId(children);
    return (
      <h1 id={id} className="text-4xl font-bold tracking-tight text-base-content mb-8 scroll-mt-20">
        {children}
      </h1>
    );
  },
  h2: ({ children }) => {
    const id = createHeadingId(children);
    return (
      <h2 id={id} className="text-3xl font-semibold text-base-content mt-12 mb-6 scroll-mt-20 pb-2 border-b border-base-200">
        {children}
      </h2>
    );
  },
  h3: ({ children }) => {
    const id = createHeadingId(children);
    return (
      <h3 id={id} className="text-2xl font-medium text-base-content mt-8 mb-4 scroll-mt-20">
        {children}
      </h3>
    );
  },
  h4: ({ children }) => {
    const id = createHeadingId(children);
    return (
      <h4 id={id} className="text-xl font-medium text-base-content mt-6 mb-3 scroll-mt-20">
        {children}
      </h4>
    );
  },

  // Paragraphs and lists
  p: ({ children }) => (
    <p className="text-base-content/80 leading-7 mb-4 text-lg">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-6 space-y-2 mb-6 text-base-content/80 text-lg">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 space-y-2 mb-6 text-base-content/80 text-lg">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="leading-7">
      {children}
    </li>
  ),

  // Blockquotes for tips and important notes
  blockquote: ({ children }) => (
    <div className="rounded-lg bg-base-200 p-6 my-6 border-l-4 border-primary">
      <div className="text-lg text-base-content/80">
        {children}
      </div>
    </div>
  ),

  // Links
  a: ({ href, children }) => {
    // Wenn es ein interner Anker-Link ist
    if (href?.startsWith('#')) {
      return (
        <a 
          href={href} 
          className="text-primary hover:text-primary/80 transition-colors"
          onClick={(e) => {
            e.preventDefault();
            const element = document.getElementById(href.slice(1));
            if (element) {
              const navbarHeight = 64;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

              window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
              });
            }
          }}
        >
          {children}
        </a>
      );
    }
    // Für externe Links
    return (
      <Link to={href} className="text-primary hover:text-primary/80 transition-colors">
        {children}
      </Link>
    );
  },

  // Images
  img: ({ src, alt }) => (
    <figure className="my-8">
      <img 
        src={src} 
        alt={alt} 
        className="rounded-xl w-full shadow-lg"
      />
      {alt && (
        <figcaption className="text-center text-sm text-base-content/60 mt-3">
          {alt}
        </figcaption>
      )}
    </figure>
  ),

  // Code blocks
  pre: ({ children }) => (
    <pre className="bg-base-200 rounded-lg p-4 overflow-x-auto my-6">
      {children}
    </pre>
  ),
  code: ({ children }) => (
    <code className="bg-base-200 rounded px-1.5 py-0.5 text-sm font-mono">
      {children}
    </code>
  ),

  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto my-6">
      <table className="table table-zebra w-full">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th className="bg-base-200 p-3 text-base-content font-semibold">{children}</th>,
  td: ({ children }) => <td className="p-3">{children}</td>,
};

// Function to process markdown content and remove the table of contents section
const processMarkdown = (content) => {
  // Split content by lines
  const lines = content.split('\n');
  let processedLines = [];
  let skipMode = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Check for table of contents section
    if (line.startsWith('## Inhaltsverzeichnis') || line.startsWith('# Inhaltsverzeichnis')) {
      skipMode = true;
      continue;
    }
    
    // Check for the end of table of contents (next heading)
    if (skipMode && line.startsWith('#')) {
      skipMode = false;
    }
    
    // Add line if not in skip mode
    if (!skipMode) {
      processedLines.push(line);
    }
  }

  return processedLines.join('\n');
};

const WikiArticle = ({ content, metadata }) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Process the markdown content to remove table of contents
  const processedContent = processMarkdown(content);

  return (
    <WikiLayout>
      <article className="py-8">
        {/* Hero Section with Image */}
        {metadata.image && (
          <div className="relative h-[40vh] min-h-[320px] w-full mb-12 overflow-hidden rounded-2xl bg-base-200">
            <img
              src={metadata.image}
              alt={metadata.title}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-base-100/90 to-transparent" />
          </div>
        )}

        {/* Article Header */}
        <header className="mb-12">
          <Breadcrumbs title={metadata.title} />
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-base-content">
            {metadata.title}
          </h1>
          {metadata.description && (
            <p className="mt-4 text-xl text-base-content/70 leading-8">
              {metadata.description}
            </p>
          )}
        </header>

        {/* Article Content */}
        <div className="prose prose-lg prose-slate max-w-none">
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]} 
            components={components}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      </article>
    </WikiLayout>
  );
};

export default WikiArticle; 